import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { countriesOptions } from '@/locale';
import { PAYIN_AUDIENCE_TYPES, PAYIN_SCHOOL_SEGMENTS } from '@/constants/constants';
import {
  inputRegex, optionalFieldWithRegex, requiredFieldWithRegex, requiredEmailWithRegex, errorMessages
} from '@/reusableInputConfig/reusableInputConfig';

interface Option {
  value: string;
  displayValue: string;
}

interface FormField {
  label: string;
  id: string;
  type: string;
  options?: Option[];
  description?: string;
  greyDescription?: boolean;
}

type FormFields = Record<string, FormField>;

export const formFields: FormFields = {
  firstName: {
    label: 'First name',
    id: 'firstName',
    type: 'text',
  },
  lastName: {
    label: 'Last name',
    id: 'lastName',
    type: 'text',
  },
  email: {
    label: 'Email address',
    id: 'email',
    type: 'email',
  },
  confirmEmail: {
    label: 'Confirm your email address',
    id: 'confirmEmail',
    type: 'email',
  },
  jobTitle: {
    label: 'What is your job title?',
    id: 'jobTitle',
    type: 'text',
  },
  establishmentType: {
    label: 'Select your organisation type',  // Intentional! See DF-431 for more details
    id: 'establishmentType',
    description: 'Please select',
    greyDescription: true,
    type: 'text',
    options: [
      { value: PAYIN_SCHOOL_SEGMENTS.NURSERY, displayValue: 'Nursery/Early Years Setting' },
      { value: PAYIN_SCHOOL_SEGMENTS.PRIMARY, displayValue: 'Primary School' },
      { value: PAYIN_SCHOOL_SEGMENTS.SECONDARY, displayValue: 'Secondary School' },
      { value: PAYIN_SCHOOL_SEGMENTS.COLLEGE, displayValue: 'College or Sixth Form' },
      { value: PAYIN_SCHOOL_SEGMENTS.YOUTH_GROUP, displayValue: 'Youth Group' },
    ],
  },
  organisationName: {
    label: 'Organisation name',
    id: 'organisationName',
    type: 'text',
  },
  establishmentName: {
    label: 'Organisation name', // Intentional! See DF-431 for more details
    id: 'establishmentName',
    type: 'text',
  },
  address1: {
    label: 'Address line 1',
    id: 'address1',
    type: 'text',
  },
  address2: {
    label: 'Address line 2',
    id: 'address2',
    type: 'text',
  },
  address3: {
    label: 'Address line 3',
    id: 'address3',
    type: 'text',
  },
  town: {
    label: 'Town or city',
    id: 'town',
    type: 'text',
  },
  postcode: {
    label: 'Postcode',
    id: 'postcode',
    type: 'text',
  },
  country: {
    label: 'Country',
    id: 'country',
    options: countriesOptions,
    type: 'text',
  },
};

export const getAddressSectionHeaderText = (audience: string | undefined, isUKOnlyPostage: boolean) => {
  if (isUKOnlyPostage) {
    return 'Your billing address (UK only)';
  }

  switch (audience) {
    // Intentional! See DF-431 for more details
    case PAYIN_AUDIENCE_TYPES.SCHOOL:
    case PAYIN_AUDIENCE_TYPES.WORK:
      return 'Your organisation details';
    case PAYIN_AUDIENCE_TYPES.PUBLIC:
      return 'Your postal address';
    default: // default covers Single and Monthly
      return 'Your address';
  }
};

export const getRules = () => ({
  [formFields.firstName.id]: requiredFieldWithRegex(inputRegex.NAME.PATTERN, inputRegex.NAME.ERROR, errorMessages.firstName),

  [formFields.lastName.id]: requiredFieldWithRegex(inputRegex.NAME.PATTERN, inputRegex.NAME.ERROR, errorMessages.lastName),

  [formFields.email.id]: requiredEmailWithRegex(inputRegex.EMAIL.PATTERN, inputRegex.EMAIL.ERROR, errorMessages.email),

  [formFields.confirmEmail.id]: requiredFieldWithRegex(inputRegex.EMAIL.PATTERN, inputRegex.EMAIL.ERROR, errorMessages.confirmEmailRequired, 255)
    .oneOf([yup.ref('email')], errorMessages.confirmEmail),

  [formFields.jobTitle.id]: optionalFieldWithRegex(inputRegex.JOB.PATTERN, inputRegex.JOB.ERROR),

  [formFields.establishmentType.id]: yup.string().when('$audience', {
    is: 'school',
    then: schema => schema.required(errorMessages.establishmentType),
    otherwise: schema => schema.optional(),
  }),

  [formFields.organisationName.id]: yup.string().when('$audience', {
    is: 'work',
    then: () => requiredFieldWithRegex(inputRegex.NAME.PATTERN, inputRegex.NAME.ERROR, errorMessages.organisationName),
    otherwise: schema => schema.optional(),
  }),

  [formFields.establishmentName.id]: yup.string().when('$audience', {
    is: 'school',
    then: () => requiredFieldWithRegex(inputRegex.NAME.PATTERN, inputRegex.NAME.ERROR, errorMessages.establishmentName),
    otherwise: schema => schema.optional(),
  }),

  [formFields.address1.id]: requiredFieldWithRegex(inputRegex.ADDRESS.PATTERN, inputRegex.ADDRESS.ERROR, errorMessages.address1),

  [formFields.address2.id]: optionalFieldWithRegex(inputRegex.ADDRESS.PATTERN, inputRegex.ADDRESS.ERROR),

  [formFields.address3.id]: optionalFieldWithRegex(inputRegex.ADDRESS.PATTERN, inputRegex.ADDRESS.ERROR),

  [formFields.town.id]: requiredFieldWithRegex(inputRegex.ADDRESS.PATTERN, inputRegex.ADDRESS.ERROR, errorMessages.town),

  [formFields.country.id]: yup.string().required(errorMessages.country),

  [formFields.postcode.id]: yup.string()
    .when(formFields.country.id, {
      is: 'GB',
      then: schema => schema
        .required(inputRegex.UK_POSTCODE.ERROR)
        .matches(
          inputRegex.UK_POSTCODE.PATTERN,
          inputRegex.UK_POSTCODE.ERROR
        ),
      otherwise: schema => schema
        .required(inputRegex.NON_UK_POSTCODE.ERROR)
        .matches(
          inputRegex.NON_UK_POSTCODE.PATTERN,
          inputRegex.NON_UK_POSTCODE.ERROR
        ),
    }),
});

export const getFormSchema = () => (
  yup.object().shape(getRules())
);

export const getFormResolver = () => (
  yupResolver(getFormSchema())
);
